import {
  VpcPeering,
  VpcPeeringTunnel,
  VpcPeeringWritePatch,
  isPeeringShaped,
  useVpcPeeringMode
} from '@hevoio/hevo-ui-toolkit';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useRef } from 'react';
import useVpcPeeringSpec from '../../../../../core/hooks/useVpcPeeringSpec';

/**
 * `useSSH` is the ONLY tunnel flag. Peering fills the same triple rather than
 * setting a second field, and which mode a config is in is derived from
 * `sshUser` (INF-1743 decision 1, matching Edge's `isPeeringShaped`).
 */
export interface SSHFormValues {
  useSSH: boolean;
  sshIp?: string;
  sshPort?: string | number;
  sshUser?: string;
}

export interface UseSSHConnectionModeProps {
  /**
   * The real source/destination type hermes matches on (`AWS_RDS_POSTGRES`,
   * `REDSHIFT`, …). Omit it and no peering lookup happens, so the form behaves
   * exactly as it did before this feature.
   */
  entityType?: string;
  /**
   * Editing an existing entity. Peering only ever defaults itself on for a NEW
   * one: an edit form for a source that connects directly has the same empty
   * form values as a fresh one, so without this the default would change how a
   * working pipeline connects.
   */
  isEditing?: boolean;
}

export interface UseSSHConnectionModeReturn {
  peering?: VpcPeering;
  /** No answer yet — render a placeholder rather than deciding. */
  peeringLoading: boolean;
  peeringOffered: boolean;
  peeringAvailable: boolean;
  usePeering: boolean;
  tunnel?: VpcPeeringTunnel;
  hostRefreshed: boolean;
  /** True only for a tunnel that is NOT peering-shaped. */
  useManualSSH: boolean;
  onSelectHost: (host: string) => Promise<void>;
  onTogglePeering: (checked: boolean) => Promise<void>;
  onToggleManualSSH: (checked: boolean) => Promise<void>;
}

/**
 * Owns every write to `useSSH`.
 *
 * Peering and manual SSH are two views over one field, so the thing that goes
 * wrong is two uncoordinated writers: ElasticSearch has its own `useSSH` effect
 * (HTTPS and SSH are mutually exclusive there) and racing it silently reverted
 * the customer's click. Keeping the peering write, the manual-SSH write and the
 * create-flow default in one place is what makes that orderable rather than
 * accidental — the component below only renders what this returns.
 */
export function useSSHConnectionMode({
  entityType,
  isEditing = false
}: UseSSHConnectionModeProps): UseSSHConnectionModeReturn {
  const { values, setFieldValue } = useFormikContext<SSHFormValues>();

  // Fetches the served spec; useVpcPeeringMode then derives the form state from
  // it. `peeringLoading` is the window where we deliberately have no answer:
  // Standard reconstructs the spec from team settings plus /peering/requests, a
  // round trip Edge does not make because hermes embeds the key in the template
  // the form already fetches.
  const { peering, loading: peeringLoading } = useVpcPeeringSpec({ entityType });

  // Standard's half of the peering contract: camelCase field names, no write
  // gate, and the port stored as a string because that is what the existing
  // HdFormikTextField expects. Only the keys present in the patch are written.
  // `sshEnabled` maps onto `useSSH` — the one tunnel flag — and the mode falls
  // out of the `user` the same patch writes.
  const write = useCallback(
    async (patch: VpcPeeringWritePatch) => {
      if ('sshEnabled' in patch) await setFieldValue('useSSH', !!patch.sshEnabled);
      if ('host' in patch) await setFieldValue('sshIp', patch.host ?? '');
      if ('port' in patch)
        await setFieldValue('sshPort', patch.port === null ? '' : String(patch.port));
      if ('user' in patch) await setFieldValue('sshUser', patch.user ?? '');

      return true;
    },
    [setFieldValue]
  );

  const {
    peeringOffered,
    peeringAvailable,
    usePeering,
    tunnel,
    hostRefreshed,
    onSelectHost,
    onTogglePeering
  } = useVpcPeeringMode({
    peering,
    // The hook derives peering-ness itself, from this flag plus the saved user
    // against the served `peering.user` — there is no separate field to pass.
    sshEnabled: values.useSSH,
    savedHost: values.sshIp,
    savedPort: values.sshPort,
    savedUser: values.sshUser,
    isCreate: !isEditing,
    write
  });

  // The customer having touched the switch themselves is what makes "off" stick.
  const userToggledRef = useRef(false);
  // Bounded, because the effect re-runs while `usePeering` is still false: if a
  // write never lands (a guarded write, a spec whose user does not match) an
  // unbounded version spins. Two is enough for the case it exists for — the
  // first application, plus one re-apply after a form re-initialise wipes it.
  const defaultAttemptsRef = useRef(0);

  // Peering is ON by default for a new entity whenever a usable tunnel is on
  // offer (ticket decision 4), and only when nothing is configured yet.
  //
  // `values.useSSH` is that "nothing configured yet" condition. The toolkit's
  // own default effect guards on `sshEnabled`/`savedUser`, but calling
  // `onTogglePeering` from here bypasses it, so the condition has to be
  // restated or a customer's own bastion gets overwritten. Guarding on the rest
  // of the triple instead would break the seeded-port case — `getDefaultConfig`
  // seeds `sshPort` on five connectors without ever setting `useSSH`.
  //
  // `values.sshUser` guards the re-apply specifically. The case the re-apply
  // exists for is a form re-initialise, which wipes the whole triple including
  // the user; a SIBLING effect clearing only the flag leaves it set. That is
  // what tells ElasticSearch's HTTPS toggle apart from a re-initialise.
  useEffect(() => {
    if (
      isEditing ||
      userToggledRef.current ||
      !peeringAvailable ||
      usePeering ||
      values.useSSH ||
      values.sshUser
    ) {
      return;
    }

    if (defaultAttemptsRef.current >= 2) {
      return;
    }

    defaultAttemptsRef.current += 1;
    onTogglePeering(true);
  }, [isEditing, peeringAvailable, usePeering, values.useSSH, values.sshUser, onTogglePeering]);

  const onTogglePeeringByCustomer = useCallback(
    async (checked: boolean) => {
      userToggledRef.current = true;
      await onTogglePeering(checked);
    },
    [onTogglePeering]
  );

  // Turning manual SSH on has to clear a peering-shaped triple, or the derived
  // mode would snap straight back to PEERING and the customer would be looking
  // at a switch that refuses to move. Unreachable while the spec is served (the
  // switch is disabled under peering), but reachable when the spec is absent and
  // the saved config is peering-shaped.
  const onToggleManualSSH = useCallback(
    async (checked: boolean) => {
      await setFieldValue('useSSH', checked);

      if (checked && isPeeringShaped({ sshEnabled: true, sshUser: values.sshUser })) {
        await setFieldValue('sshIp', '');
        await setFieldValue('sshPort', '');
        await setFieldValue('sshUser', '');
      }
    },
    [setFieldValue, values.sshUser]
  );

  return {
    peering,
    peeringLoading,
    peeringOffered,
    peeringAvailable,
    usePeering,
    tunnel,
    hostRefreshed,
    // Manual SSH is the tunnel that is NOT peering-shaped. Both toggles read off
    // the same `useSSH` field, so this is what keeps the manual switch from
    // showing on while peering fills the fields.
    useManualSSH: values.useSSH && !usePeering,
    onSelectHost,
    onTogglePeering: onTogglePeeringByCustomer,
    onToggleManualSSH
  };
}
